import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Container } from '@pixi/react';
import { Text as PixiText, Rectangle } from 'pixi.js';
import { PgWheelThemeConfigs } from '../../../../../../constants/pg-wheel-theme-configs';
import { soundService, SoundTypes } from '../../../../../../../services/soundService';
import { BoostedJackpotWinPopupViewProps } from '../../../index.types';
import { isoStandard } from '../../../../../../helpers';
import { Spine } from '../../../../../../components';
import { useStore, useTranslation } from '../../../../../../../hooks';
import { isMobile } from '../../../../../../../helpers';
import { T } from '../../../../../../../constants';
import gsap from 'gsap';

export const BoostedJackpotWinPopupView: React.FC<BoostedJackpotWinPopupViewProps> = observer(({
  setIsWheel,
  spinWheelWinAmount,
  setSpinWheelWinAmount,
  setIsBoostedJackpotWinPopup,
  amountSlotRef,
  congratSlotRef,
  bgSpineRef,
  buttonSlotRef,
}) => {
  const congratsAmountRef: any = useRef(null);
  const amountTextRef = useRef<any>(null);
  const freeSpinLabelRef = useRef<any>(null);
  const buttonLabelRef = useRef<any>(null);
  const buttonSpineRef = useRef<any>(null);
  const congratsAmountSpineRef = useRef<any>(null);
  const t = useTranslation();
  const { boostedJackpotStore, gameStore, postMessageStore } = useStore();
  const { currencySymbol } = gameStore;
  const partnerId = gameStore.partnerId;
  const {
    setIsBoostedPopupOpen,
    setHasBoostedJackpot,
    setIsWheelZoomOut,
    setSectorStopIndex,
    spinWheelWinIsFreeBet,
  } = boostedJackpotStore;
  const isClosingRef = useRef(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const strokeColorSecondary = '#b28927';

  const formattedWinAmount = useMemo(() => {
    return isoStandard(Number(spinWheelWinAmount || 0), true);
  }, [spinWheelWinAmount]);

  useEffect(() => {
    soundService.play(SoundTypes.WheelWinPopup);
    return () => {
      soundService.stop(SoundTypes.WheelWinPopup);
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  const playStartThenIdle = (spine: Spine, fallbackIdle = 'idle', shouldLoop = false) => {
    const hasStart = spine.state?.hasAnimation('start');
    const startDuration = hasStart ? (spine.getAnimationDuration?.('start') || 0) : 0;

    if (hasStart && startDuration > 0) {
      spine.play('start', false);
      spine.addListener(
        'complete',
        () => {
          if (spine.state?.hasAnimation(fallbackIdle)) {
            spine.play(fallbackIdle, shouldLoop);
          }
        },
        null,
        '',
        true
      );
      return;
    }

    if (spine.state?.hasAnimation(fallbackIdle)) {
      spine.play(fallbackIdle, shouldLoop);
      return;
    }

    if (spine.state?.hasAnimation('loop')) {
      spine.play('loop', true);
    }
  };

  useEffect(() => {
    const slot = congratSlotRef?.current;
    if (!slot) return;

    const congratsTitle = new PixiText(t(T.CONGRATULATIONS), {
      fontSize: isMobile() ? 30 : 60,
      align: 'center',
      fill: '#fffae0',
      stroke: '#b28927',
      strokeThickness: 8,
      dropShadow: true,
      dropShadowColor: '#573321',
      dropShadowBlur: 0,
      dropShadowDistance: 4.34,
      dropShadowAngle: Math.PI / 2,
      fontWeight: '900',
      fontFamily: 'Verdana',
    } as any);
    congratsTitle.anchor.set(0.5);
    congratsTitle.y = isMobile() ? -120 : -150;
    congratsTitle.resolution = 2;

    const youHaveWon = new PixiText(t(T.YOU_HAVE_WON), {
      fontSize: isMobile() ? 26 : 38,
      align: 'center',
      fill: '#ffffff',
      stroke: strokeColorSecondary,
      dropShadow: true,
      dropShadowColor: '#573321',
      dropShadowBlur: 0,
      dropShadowDistance: 4.34,
      dropShadowAngle: Math.PI / 2,
      fontWeight: '800',
      fontFamily: 'Verdana',
    } as any);
    youHaveWon.anchor.set(0.5);
    youHaveWon.y = isMobile() ? -62 : -45;
    youHaveWon.resolution = 2;

    slot.addChild(congratsTitle);
    slot.addChild(youHaveWon);

    return () => {
      try { slot.removeChild(congratsTitle); } catch { }
      try { slot.removeChild(youHaveWon); } catch { }
      try { congratsTitle.destroy(); } catch { }
      try { youHaveWon.destroy(); } catch { }
    };
  }, [congratSlotRef]);

  useEffect(() => {
    if (!buttonSlotRef?.current) return;
    const container = buttonSlotRef.current;

    // Clear any leftover SPIN-phase children (spine + label) that mountWheelSpinButton
    // added but never removed when BoostedJackpotWheel unmounted.
    while (container.children?.length) {
      try { container.removeChildAt(0); } catch { break; }
    }

    container.y = isMobile() ? -90 : spinWheelWinIsFreeBet ? -130 : -180;
    if (isMobile()) container.scale.set(0.9);

    const wheelThemeButton: Spine = new Spine({ key: 'boostedWheelThemeButton' });
    playStartThenIdle(wheelThemeButton, 'default', true);
    buttonSpineRef.current = wheelThemeButton;
    container.addChild(wheelThemeButton);

    const buttonTextColor = PgWheelThemeConfigs[partnerId].buttonTextColor;
    const buttonShadowColor = PgWheelThemeConfigs[partnerId].buttonShadowColor;
    const buttonDropShadow = PgWheelThemeConfigs[partnerId].buttonDropShadow;

    const buttonLabel = new PixiText(t(T.CONTINUE).toUpperCase(), {
      fontSize: 28,
      align: 'center',
      fill: buttonTextColor,
      stroke: buttonTextColor,
      strokeThickness: 2,
      dropShadow: buttonDropShadow,
      dropShadowColor: buttonShadowColor,
      dropShadowBlur: 0,
      dropShadowDistance: 4.34,
      dropShadowAngle: Math.PI / 2,
      fontFamily: 'Verdana',
    } as any);
    buttonLabel.anchor.set(0.5);
    buttonLabelRef.current = buttonLabel;
    container.addChild(buttonLabel);

    // Hover / press visual feedback via Pixi imperative events on the slot container.
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointerover', () => { container.children?.[0]?.play?.('hover', true); });
    container.on('pointerout', () => { container.children?.[0]?.play?.('default', true); });
    container.on('pointerdown', () => { container.children?.[0]?.play?.('pressed', true); });
    container.on('pointerup', () => { container.children?.[0]?.play?.('default', true); });

    return () => {
      gsap.killTweensOf(container);
      try {
        gsap.killTweensOf(container.scale);
        if (!container.destroyed) {
          container.y = 0;
          container.alpha = 1;
          container.visible = true;
          container.scale?.set(1);
        }
        container.removeAllListeners();
        container.removeChild(wheelThemeButton);
        container.removeChild(buttonLabel);
        wheelThemeButton.destroy();
        buttonLabel.destroy();
      } catch { }
      buttonSpineRef.current = null;
    };
  }, []);

  useEffect(() => {
    const amountParent = amountSlotRef?.current || congratsAmountRef.current;
    if (amountParent) {
      const congratsAmount: Spine = new Spine({ key: 'boostedCongratsAmount' });
      playStartThenIdle(congratsAmount, 'idle', false);
      congratsAmountSpineRef.current = congratsAmount;

      const amountDisplayLen = formattedWinAmount.length;
      const amountFontSize = isMobile()
        ? amountDisplayLen <= 7 ? 48 : 32
        : amountDisplayLen >= 13 ? 56 : amountDisplayLen >= 10 ? 72 : amountDisplayLen >= 7 ? 84 : 96;

      const amountText = new PixiText(`${formattedWinAmount} ${currencySymbol}`, {
        fontSize: amountFontSize,
        align: 'center',
        fill: '#fffae0',
        stroke: '#b28927',
        strokeThickness: 8,
        dropShadow: true,
        dropShadowColor: '#573321',
        dropShadowBlur: 0,
        dropShadowDistance: 4.34,
        dropShadowAngle: Math.PI / 2,
        fontWeight: '900',
        fontFamily: 'Verdana',
      } as any);
      amountText.anchor.set(0.5);
      amountTextRef.current = amountText;
      const addedToAmountSlot = congratsAmount.addChildToSlot('_amountSlot', amountText as any);
      if (!addedToAmountSlot) {
        congratsAmount.addChildToSlot('_numberSlot', amountText as any);
      }

      if (spinWheelWinIsFreeBet) {
        const freeSpinFontSize = isMobile() ? 32 : 48;
        const freeSpinLabel = new PixiText(t(T.FREE_BET), {
          fontSize: freeSpinFontSize,
          align: 'center',
          fill: ['#fecd69', '#ffdb93', '#caa65e'],
          stroke: strokeColorSecondary,
          strokeThickness: 8,
          letterSpacing: 8,
          dropShadow: true,
          dropShadowColor: '#573321',
          dropShadowBlur: 0,
          dropShadowDistance: 4.34,
          dropShadowAngle: Math.PI / 2,
          fontWeight: '900',
          fontFamily: 'Verdana',
        } as any);
        freeSpinLabel.anchor.set(0.5);
        freeSpinLabel.y = isMobile() ? 60 : 90;
        freeSpinLabel.resolution = 2;
        freeSpinLabelRef.current = freeSpinLabel;
        congratsAmount.addChild(freeSpinLabel as any);
      }

      amountParent.addChild(congratsAmount);
    }
  }, [amountSlotRef, spinWheelWinIsFreeBet, formattedWinAmount, strokeColorSecondary]);

  useEffect(() => {
    if (amountTextRef.current) {
      amountTextRef.current.text = `${formattedWinAmount} ${currencySymbol}`;
    }
  }, [formattedWinAmount, spinWheelWinIsFreeBet]);
  const startCloseAnimations = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;

    // 1. Button: identical to playHideIfExists(buttonSlotRef) — uses buttonSpineRef
    //    (the AWESOME spine instance) directly, not children[0] which would be the
    //    stale SPIN spine left by mountWheelSpinButton cleanup.
    const buttonSpine = buttonSpineRef.current;
    const hideDurationSec = buttonSpine?.getAnimationDuration?.('hide') || 0.3;
    if (buttonSpine?.state?.hasAnimation?.('hide')) {
      buttonSpine.play('hide', false);
      gsap.to(buttonLabelRef.current, { alpha: 0, duration: hideDurationSec, ease: 'linear' });
      // Collapse the slot container once the spine animation finishes.
      gsap.to(buttonSlotRef.current, { alpha: 0, duration: 0.05, delay: hideDurationSec });
    } else if (buttonSlotRef?.current) {
      gsap.to(buttonSlotRef.current, { alpha: 0, duration: 0.2, ease: 'power1.in' });
    }

    // 2. Amount slot: scale-collapse + alpha fade (mirrors congrats text style).
    const amountContainer = amountSlotRef?.current || congratsAmountRef.current;
    const amountDurationSec = 0.6;
    if (amountContainer) {
      gsap.to(amountContainer.scale, { x: 0, y: 0, duration: amountDurationSec, ease: 'back.in(1.4)' });
      gsap.to(amountContainer, { alpha: 0, duration: 0.4, ease: 'power1.in', delay: 0.2 });
    }

    // 3. Congrats texts: zoom-out scale collapse + alpha fade.
    const congratsContainer = congratSlotRef?.current;
    if (congratsContainer) {
      gsap.to(congratsContainer.scale, { x: 0, y: 0, duration: 0.75, ease: 'back.in(1.4)' });
      gsap.to(congratsContainer, { alpha: 0, duration: 0.55, ease: 'power1.in', delay: 0.2 });
    }

    // Wait for the longest animation before unmounting.
    const buttonDurationMs = Math.ceil(hideDurationSec * 1000);
    const amountDurationMs = Math.ceil(amountDurationSec * 1000);

    if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = window.setTimeout(() => {
      soundService.stop(SoundTypes.WheelWinPopup);
      setIsBoostedJackpotWinPopup(false);
      setSpinWheelWinAmount(0);
      setSectorStopIndex(null);
      setIsWheelZoomOut(false);
      setIsWheel(false);
      setIsBoostedPopupOpen(false);
      setHasBoostedJackpot(false);
      postMessageStore.sendResumeGame();
      postMessageStore.sendUpdateBalance();
    }, Math.max(buttonDurationMs, amountDurationMs, 1000));
  };

  const handleButtonPointerUp = (e: any) => {
    e?.stopPropagation?.();
    startCloseAnimations();
  };

  const handleKeyDown = useCallback((e: any) => {
    if (e.code === 'Space' || e.code === 'Enter') {
      e.preventDefault();
      startCloseAnimations();
    };
  }, []);


  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <Container pointerup={handleButtonPointerUp} interactive
      x={0} y={0}
      hitArea={new Rectangle(-window.innerWidth / 2, -window.innerHeight / 2, window.innerWidth, window.innerHeight)}>
      {!amountSlotRef && <Container y={52} ref={congratsAmountRef} />}
    </Container>
  );
});
